import Layout from "../../hocs/Layout";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import { setLoader, removeLoader } from "../../redux/actions/loader";
import {
  get_paises,
  get_provincias,
  get_municipios,
  get_poblaciones,
  get_tipovia,
} from "../../redux/actions/auxiliares";
import { Button, Col, Form, Row, Card, Container } from "react-bootstrap";
import { TextInput, SelectInput } from "../../components/inputs/Inputs";
import { get_datos_oferta_erp, set_oferta_erp } from "../../services/ofertas";
import {
  get_partner_by_id,
  get_partner_by_vat_with_warning,
  find_or_create_partner,
  set_partner_erp,
} from "../../services/partner";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";

import { toast } from "react-toastify";
import { buildPartnerSchema } from "../../utils/validationSchemas";
import { AddressFields } from "../../components/inputs/AddressFields";
import { useAddressFields } from "../../components/hooks/useAddressFields";
import OfferWizardWrapper from "../../components/ofertas/OfferWizardWrapper";
import { useOfferWizard } from "../../components/ofertas/OfferWizardContext";

const normalizeVat = (vat, countryId) => {
  const normalizedVat = String(vat || "").trim().toUpperCase();
  if (!normalizedVat) return "";

  const expectedPrefix = Number(countryId) === 67 ? "ES" : "PS";

  if (normalizedVat.startsWith("ES") || normalizedVat.startsWith("PS")) {
    return `${expectedPrefix}${normalizedVat.substring(2)}`;
  }

  return `${expectedPrefix}${normalizedVat}`;
};

const buildVatCandidates = (vat, countryId) => {
  const rawVat = String(vat || "").trim().toUpperCase();
  if (!rawVat) return [];

  const candidates = new Set([rawVat]);
  if (countryId !== undefined && countryId !== null && countryId !== "") {
    candidates.add(normalizeVat(rawVat, countryId));
  }

  if (!rawVat.startsWith("ES") && !rawVat.startsWith("PS")) {
    candidates.add(`ES${rawVat}`);
    candidates.add(`PS${rawVat}`);
  }

  return [...candidates];
};

const normalizeTipoPersona = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";

  if (raw === "fisica" || raw === "física") return "fisica";
  if (raw === "juridica" || raw === "jurídica") return "juridica";

  if (raw.includes("fis")) return "fisica";
  if (raw.includes("jur")) return "juridica";
  return "";
};

const normalizeSubTypeOffer = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const extractSubtypeText = (value) => {
  if (Array.isArray(value)) {
    if (value.length > 1) return String(value[1] || value[0] || "");
    return String(value[0] || "");
  }
  if (value && typeof value === "object") {
    return String(value.name || value.display_name || value.label || value.value || value.id || "");
  }
  return String(value || "");
};

const isCambioTitularSubtype = (value) => {
  const normalized = normalizeSubTypeOffer(value);
  return (
    normalized.startsWith("cambio_titular")
    || normalized.startsWith("cambio_de_titular")
    || (normalized.startsWith("cambio") && normalized.includes("titular"))
  );
};

const normalizePostalMode = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "email";
  if (raw === "postal" || raw === "email" || raw === "postal+email") return raw;
  // Legacy booleans from older records
  if (value === true) return "postal";
  if (value === false) return "email";
  return raw;
};

const hasPostalDelivery = (value) => {
  const mode = normalizePostalMode(value);
  return mode === "postal" || mode === "postal+email";
};

const inferTipoPersonaFromPartner = (partner, vat) => {
  const cifnif = String(partner?.cifnif || "").trim().toUpperCase();
  if (cifnif === "NI") return "fisica";
  if (cifnif === "CI" || cifnif === "CIF") return "juridica";

  const cleanVat = String(vat || "")
    .trim()
    .toUpperCase()
    .replace(/\s|-/g, "")
    .replace(/^(ES|PS)/, "");

  if (/^[XYZ]\d{7}[A-Z]$/.test(cleanVat) || /^\d{8}[A-Z]$/.test(cleanVat)) {
    return "fisica";
  }

  if (/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(cleanVat)) {
    return "juridica";
  }

  return "";
};

const OfertaPartnerForm = ({
  paises,
  provincias,
  municipios,
  poblaciones,
  tipovia,
  get_paises,
  get_provincias,
  get_municipios,
  get_poblaciones,
  get_tipovia,
  setLoader,
  removeLoader,
  user,
}) => {
  const params = useParams();
  const sector_type = params.sector_type;
  const { setActionHandler, setIsSubmitting } = useOfferWizard();

  useEffect(() => {
    const get_datos_iniciales = async () => {
      if (!params.oferta_id || params.oferta_id === "null" || params.oferta_id === 'undefined') {
        removeLoader();
        return;
      }
      setLoader("Cargando datos iniciales");
      await get_paises();
      await get_tipovia();
      removeLoader();
      setOfertaErp(await get_datos_oferta_erp(params.oferta_id));
    };
    get_datos_iniciales();
    // window.scrollTo(0, 0);
  }, [params.oferta_id, get_paises, get_tipovia, setLoader, removeLoader]);

  const defaultValues = {
    cliente_tipo_persona: "",
    cliente_nombre: "",
    cliente_apellidos: "",
    cliente_pais: 67,
    cliente_vat: "",
    cliente_telefono_1: "",
    cliente_telefono_2: "",
    cliente_email_1: "",
    cups_aclarador: "",
    cups_nv: "",
    cups_pt: "",
    cups_pu: "",
    cups_es: "",
    cups_pnp: "",
    cups_dp: "",
    cups_id_pais: "",
    cups_id_provincia: "",
    cups_id_municipi: "",
    cups_id_poblacio: "",
    cups_tv: "",
    comPostal: false,
    notificacionPostal: false,
  };

  const partnerSchema = useMemo(
    () => buildPartnerSchema({ isInternalAgent: Boolean(user?.is_internal_agent || user?.is_call_agent) }),
    [user?.is_internal_agent, user?.is_call_agent]
  );

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(partnerSchema),
  });

  const [oferta_erp, setOfertaErp] = useState(null);
  const [partner, setPartner] = useState(null);
  const [partnerWarning, setPartnerWarning] = useState(null);
  const [addressPrefillLabels, setAddressPrefillLabels] = useState({});
  const manualSearchRef = useRef(false);
  const isCambioTitular = isCambioTitularSubtype(oferta_erp?.sub_type_offer);
  const renewalSubtype = normalizeSubTypeOffer(extractSubtypeText(oferta_erp?.sub_type_offer));
  const renewalType = normalizeSubTypeOffer(
    oferta_erp?.type || oferta_erp?.type_offer || oferta_erp?.tipo_oferta
  );
  const isRenewalReadOnly =
    renewalSubtype.startsWith("renovacion")
    || renewalSubtype.includes("renovacion")
    || renewalType.startsWith("renovacion");

  const resolveTipoPersona = useCallback(
    (partnerData, vatValue) => {
      const fromOffer = normalizeTipoPersona(oferta_erp?.cliente_tipo_persona);
      if (fromOffer) return fromOffer;

      const fromPartner = inferTipoPersonaFromPartner(partnerData, vatValue);
      if (fromPartner) return fromPartner;

      return "";
    },
    [oferta_erp?.cliente_tipo_persona]
  );

  const lookupPartnerByVat = async (vat, countryId) => {
    const vatCandidates = buildVatCandidates(vat, countryId);

    for (const candidate of vatCandidates) {
      const result = await get_partner_by_vat_with_warning(candidate, params.oferta_id);
      if (result?.partner || result?.warning) {
        return { ...result, searchedVat: candidate };
      }
    }

    return {
      partner: null,
      warning: null,
      searchedVat: vatCandidates[0] || String(vat || "").trim().toUpperCase(),
    };
  };

  const {
    loadingProv: loadProv,
    loadingMuni: loadMuni,
    loadingPob: loadPob,
    error: loadError,
    fetchProvincias,
    fetchMunicipios,
    fetchPoblaciones
  } = useAddressFields({
    get_provincias,
    get_municipios,
    get_poblaciones
  });

  // Sync submitting state
  useEffect(() => {
    setIsSubmitting(isSubmitting);
  }, [isSubmitting, setIsSubmitting]);

  // When the offer loads, fetch the partner by ID (titular field) and populate the form from partner data.
  // Fall back to VAT lookup only if no titular is present.
  useEffect(() => {
    if (!oferta_erp) return;

    const envio =
      sector_type === "electrico"
        ? oferta_erp.envio_postal
        : oferta_erp.envio_postal_gas;
    const isFacturaPostal = hasPostalDelivery(envio);
    const isNotificacionPostal = hasPostalDelivery(oferta_erp.envio_postal_alt);
    setValue("comPostal", isFacturaPostal);
    setValue("notificacionPostal", isNotificacionPostal);

    const extractMany2OneId = (value) => {
      if (Array.isArray(value) && value.length > 0 && Number(value[0])) {
        return Number(value[0]);
      }
      if (typeof value === "number" && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === "string" && value.trim() !== "" && Number(value)) {
        return Number(value);
      }
      return null;
    };

    const fetchPartner = async () => {
      // In crm.polissa, partner reference can arrive as partner_id (current) or titular (legacy).
      const partnerId =
        extractMany2OneId(oferta_erp.partner_id) ||
        extractMany2OneId(oferta_erp.titular);

      if (partnerId) {
        const loaded = await get_partner_by_id(partnerId);
        if (loaded) {
          setPartner(loaded);
          // Also check for debt warning using VAT
          if (loaded.vat) {
            const warningRes = await get_partner_by_vat_with_warning(loaded.vat, params.oferta_id);
            setPartnerWarning(warningRes?.warning || null);
          }
          return;
        }
      }

      // Fallback: partner not resolved by ID, try VAT from offer
      const savedVat = String(oferta_erp.cliente_vat || "").trim();
      if (savedVat) {
        const res = await lookupPartnerByVat(savedVat, null);
        setPartner(res.partner || null);
        setPartnerWarning(res.warning || null);
      }
    };

    fetchPartner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oferta_erp, isCambioTitular, sector_type]);

  // When partner loads, fill the entire form from partner data.
  useEffect(() => {
    if (!partner) return;

    const countryId = partner.country && partner.country[0] ? partner.country[0] : 67;
    const nacionalidad = Number(countryId) === 67 ? "ES" : "PS";

    let vat = String(partner.vat || oferta_erp?.cliente_vat || "").trim();
    if (vat && !vat.startsWith("ES") && !vat.startsWith("PS")) {
      vat = `${nacionalidad}${vat}`;
    }

    const comPostal = getValues("comPostal");
    const isNotificacionPostal = hasPostalDelivery(oferta_erp?.envio_postal_alt);

    // Reset manual search flag after processing partner data
    if (manualSearchRef.current) {
      manualSearchRef.current = false;
    }

    reset({
      comPostal,
      notificacionPostal: isNotificacionPostal,
      cliente_tipo_persona: resolveTipoPersona(partner, vat),
      cliente_nombre: partner.first_name || "",
      cliente_apellidos: partner.last_name || "",
      cliente_vat: vat,
      cliente_pais: countryId,
      cliente_telefono_1: partner.phone || "",
      cliente_telefono_2: partner.mobile || "",
      cliente_email_1: partner.email || "",
      cups_aclarador: partner.aclarador || "",
      cups_nv: partner.nv || "",
      cups_pt: partner.pt || "",
      cups_pu: partner.pu || "",
      cups_es: partner.es || "",
      cups_pnp: partner.pnp || "",
      cups_dp: partner.zip || "",
      cups_id_pais: countryId,
      cups_tv: partner.tv ? partner.tv[0] : "",
    });

    const setLocation = async () => {
      setLoader("Cargando cliente");
      try {
        if (countryId) await get_provincias(countryId);
        if (partner.state_id && partner.state_id[0]) {
          await get_municipios(partner.state_id[0]);
          setValue("cups_id_provincia", partner.state_id[0]);
        }
        if (partner.id_municipi && partner.id_municipi[0]) {
          await get_poblaciones(partner.id_municipi[0]);
          setValue("cups_id_municipi", partner.id_municipi[0]);
        }
        if (partner.id_poblacio && partner.id_poblacio[0]) {
          await get_poblaciones(partner.id_municipi[0]);
          setValue("cups_id_poblacio", partner.id_poblacio[0], { shouldValidate: true });
        }
        // Save name labels for fallback display in dropdowns
        const getName = (m2o) => (Array.isArray(m2o) && m2o.length > 1 ? m2o[1] : null);
        setAddressPrefillLabels({
          provincia: getName(partner.state_id) || "",
          municipio: getName(partner.id_municipi) || "",
          poblacion: getName(partner.id_poblacio) || "",
        });
      } catch (error) {
        console.error("Error setting location cascade", error);
      } finally {
        removeLoader();
      }
    };
    setLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner, oferta_erp, isCambioTitular, resolveTipoPersona]);

  // Removed manual fetch functions as they are now in the hook

  useEffect(() => {
    if (!oferta_erp) return;
    const currentTipoPersona = getValues("cliente_tipo_persona");
    if (currentTipoPersona) return;

    const resolved = resolveTipoPersona(partner, oferta_erp?.cliente_vat);
    if (resolved) {
      setValue("cliente_tipo_persona", resolved, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [oferta_erp, partner, getValues, isCambioTitular, resolveTipoPersona, setValue]);


  const onSearchPartner = async () => {
    const data = getValues();
    // Mark this as a manual search so that useEffect will process the partner data
    manualSearchRef.current = true;
    const res = await lookupPartnerByVat(data.cliente_vat, data.cliente_pais);
    const partnerRes = res.partner;
    setPartnerWarning(res.warning || null);
    if (!partnerRes) {
      reset({
        ...defaultValues,
        cliente_tipo_persona: data.cliente_tipo_persona || "",
        cliente_vat: data.cliente_vat,
        cliente_pais: data.cliente_pais
      });
      setPartnerWarning(null);
      manualSearchRef.current = false;
    } else if (res.searchedVat) {
      setValue("cliente_vat", res.searchedVat, { shouldDirty: true, shouldValidate: true });
    }
    setPartner(partnerRes);
  };

  const onSubmit = useCallback(async () => {
    if (isRenewalReadOnly) {
      return { preventDefaultNavigation: false };
    }

    if (partnerWarning) {
      toast.error("No se puede continuar con un cliente con deuda. Comuníquese con la empresa.", {
        position: toast.POSITION.TOP_CENTER, autoClose: false });
      return { preventDefaultNavigation: true };
    }

    const elec = sector_type === "electrico";
    const data = getValues();

    // Revalidar deuda justo antes de guardar para evitar bypass por cambios manuales.
    const latestLookup = await get_partner_by_vat_with_warning(data.cliente_vat, params.oferta_id);
    if (latestLookup?.warning) {
      setPartnerWarning(latestLookup.warning);
      toast.error("No se puede continuar con un cliente con deuda. Comuníquese con la empresa.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return { preventDefaultNavigation: true };
    }

    const clienteVat = normalizeVat(data.cliente_vat, data.cliente_pais);
    const clienteEmailRequired = !(data.comPostal && data.notificacionPostal);

    if (clienteEmailRequired && !String(data.cliente_email_1 || "").trim()) {
      setError("cliente_email_1", {
        type: "required",
        message: "Requerido",
      });
      toast.error("El email es obligatorio si no están marcados ambos envíos postales.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return { preventDefaultNavigation: true };
    }

    clearErrors("cliente_email_1");

    const datos = {
      cliente_tipo_persona: data.cliente_tipo_persona,
      cliente_nombre: data.cliente_tipo_persona === "fisica" ? data.cliente_nombre : "",
      cliente_apellidos: data.cliente_apellidos,
      cliente_vat: clienteVat,
      cliente_telefono_1: data.cliente_telefono_1,
      cliente_telefono_2: data.cliente_telefono_2,
      cliente_email_1: data.cliente_email_1,
      country_id: data.cliente_pais,
      country: data.cliente_pais,
      cups_id_provincia: data.cups_id_provincia,
      cups_id_municipi: data.cups_id_municipi,
      cups_id_poblacio: data.cups_id_poblacio,
      cups_aclarador: data.cups_aclarador,
      cups_tv: data.cups_tv,
      cups_nv: data.cups_nv,
      cups_pt: data.cups_pt,
      cups_pu: data.cups_pu,
      cups_es: data.cups_es,
      cups_pnp: data.cups_pnp,
      cups_dp: data.cups_dp,
      cups_cpo: null,
      cups_cpa: null,
    };
    const datosPartnerOferta = {
      cliente_tipo_persona: data.cliente_tipo_persona,
      cliente_nombre: data.cliente_tipo_persona === "fisica" ? data.cliente_nombre : "",
      cliente_apellidos: data.cliente_apellidos,
      cliente_vat: clienteVat,
      cliente_telefono_1: data.cliente_telefono_1,
      cliente_telefono_2: data.cliente_telefono_2,
      cliente_email_1: data.cliente_email_1,
      [elec ? "envio_postal" : "envio_postal_gas"]: Boolean(data.comPostal),
      envio_postal_alt: data.notificacionPostal ? "postal" : "email",
    };
    const partnerNuevo = partner
      ? await set_partner_erp(partner.id, datos) //si se decidiese cambiar el partner que buscamos, sobreescribimos una persona con otra, manteniendo el id
      : await find_or_create_partner(datos);

    if (!partnerNuevo?.id) {
      toast.error("No se pudo guardar el cliente.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return { preventDefaultNavigation: true };
    }

    datosPartnerOferta.partner_id = partnerNuevo.id;
    if (partnerNuevo.address?.[0]) {
      datosPartnerOferta.cliente_direccion = partnerNuevo.address[0];
      datosPartnerOferta.contacto_direccion = partnerNuevo.address[0];
    }

    const ofertaActualizada = await set_oferta_erp(
      params.oferta_id,
      datosPartnerOferta,
      sector_type,
      oferta_erp?.sub_type_offer,
      clienteVat
    );

    if (!ofertaActualizada) {
      return { preventDefaultNavigation: true };
    }

    setValue("cliente_vat", clienteVat, { shouldDirty: false, shouldValidate: true });
    setPartner(partnerNuevo);

    return { preventDefaultNavigation: false };
  }, [
    isRenewalReadOnly,
    partnerWarning,
    sector_type,
    getValues,
    params.oferta_id,
    partner,
    oferta_erp,
    setValue,
    setError,
    clearErrors,
  ]);

  // Register wizard actions
  useEffect(() => {
    setActionHandler({
      onNext: () => handleSubmit(onSubmit)(),
      onSave: () => handleSubmit(onSubmit)()
    });
  }, [setActionHandler, handleSubmit, onSubmit]);

  const idPais = watch("cups_id_pais");
  const idProv = watch("cups_id_provincia");
  const idMuni = watch("cups_id_municipi");
  const tipoPersona = watch("cliente_tipo_persona");
  const clienteVat = watch("cliente_vat");
  const comPostal = watch("comPostal");
  const notificacionPostal = watch("notificacionPostal");

  useEffect(() => {
    if (!comPostal && notificacionPostal) {
      setValue("notificacionPostal", false, { shouldDirty: true, shouldValidate: true });
    }
  }, [comPostal, notificacionPostal, setValue]);

  return (
    <div className="py-4">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} md={12}>
            <Card className="detail-card border-0 shadow-lg rounded-4 overflow-hidden">
              <Card.Header className="bg-white border-bottom-0 pt-4 pb-0 text-center">
                <h2 className="text-primary fw-bold mb-0">Información del Cliente</h2>
              </Card.Header>
              <Card.Body className="p-4 p-md-5">
                {partnerWarning && (
                  <div className="alert alert-danger mb-4 shadow-sm border-0 d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
                    <div>
                      <h6 className="mb-1 fw-bold">Cliente bloqueado por deuda</h6>
                      <p className="mb-0 small">{partnerWarning}</p>
                      <p className="mb-0 small fw-bold mt-1">Comuníquese con la empresa para continuar.</p>
                    </div>
                  </div>
                )}
                {isRenewalReadOnly && (
                  <div className="alert alert-secondary mb-4 shadow-sm border-0">
                    Esta pantalla es solo lectura en renovacion.
                  </div>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <div style={isRenewalReadOnly ? { pointerEvents: "none" } : undefined}>
                    <fieldset disabled={isRenewalReadOnly}>


                      {/* Section: Document & Search */}
                      <div className="bg-light p-4 rounded-4 border border-light mb-4">
                        <Row className="g-3 align-items-end">
                          <Col lg={4} md={6}>
                            <label className="text-muted small fw-bold text-uppercase mb-2">Nacionalidad Documento</label>
                            <Controller
                              name="cliente_pais"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <Form.Select
                                  {...field}
                                  value={field.value || ""}
                                  className="bg-white shadow-sm"
                                  isInvalid={!!error}
                                  onChange={(e) => {
                                    // Ensure we send a valid number or string
                                    const val = e.target.value;
                                    field.onChange(val);
                                  }}
                                >
                                  <option value="" disabled>Seleccione país</option>
                                  {paises?.map((pais) => (
                                    <option key={pais.id} value={pais.id}>
                                      {pais.name}
                                    </option>
                                  ))}
                                  {field.value && (!paises || !paises.find(p => p.id === Number(field.value))) && (
                                    <option value={field.value} disabled>
                                      {Number(field.value) === 67 ? "España" : `País ${field.value}`}
                                    </option>
                                  )}
                                </Form.Select>
                              )}
                            />
                            {errors.cliente_pais && (
                              <div className="invalid-feedback d-block">
                                {errors.cliente_pais.message}
                              </div>
                            )}
                          </Col>
                          <Col lg={4} md={6}>
                            <label className="text-muted small fw-bold text-uppercase mb-2">Nº Documento</label>
                            <Controller
                              name="cliente_vat"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <TextInput
                                  {...field}
                                  noFloating={true}
                                  error={error?.message}
                                  disabled={Boolean(partner) && !isCambioTitular}
                                  className="bg-white shadow-sm"
                                  onChange={(e) => {
                                    const cliente_pais = getValues("cliente_pais");
                                    const prefijo = Number(cliente_pais) === 67 ? "ES" : "PS";
                                    const valor = e.target.value;
                                    if (valor.startsWith(prefijo)) {
                                      field.onChange(valor);
                                    } else if (valor.length > 0) {
                                      field.onChange(`${prefijo}${valor}`);
                                    } else {
                                      field.onChange("");
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && clienteVat) {
                                      e.preventDefault();
                                      onSearchPartner();
                                    }
                                  }}
                                />
                              )}
                            />
                          </Col>
                          <Col lg={4} md={12}>
                            <Button
                              variant="primary"
                              className="rounded-pill w-100 fw-bold shadow-sm py-2"
                              onClick={onSearchPartner}
                              disabled={!clienteVat}
                            >
                              Buscar Cliente
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      {/* Section: Personal Info */}
                      <div className="bg-light p-4 rounded-4 border border-light mb-4">
                        <Row className="g-3">
                          <Col md={12}>
                            <h5 className="fw-bold text-secondary mb-3">Datos Personales</h5>
                          </Col>
                          <Col md={4}>
                            <label className="text-muted small fw-bold text-uppercase mb-2">Tipo de persona</label>
                            <Controller
                              name="cliente_tipo_persona"
                              control={control}
                              render={({ field }) => (
                                <SelectInput
                                  {...field}
                                  noFloating={true}
                                  error={errors.cliente_tipo_persona?.message}
                                  className="bg-white shadow-sm"
                                >
                                  <option key="null" value="" disabled>Seleccione tipo</option>
                                  <option value="fisica">Física</option>
                                  <option value="juridica">Jurídica</option>
                                </SelectInput>
                              )}
                            />
                          </Col>
                          {tipoPersona === "fisica" && (
                            <Col md={4}>
                              <label className="text-muted small fw-bold text-uppercase mb-2">Nombre</label>
                              <Controller
                                name="cliente_nombre"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                  <TextInput
                                    {...field}
                                    noFloating={true}
                                    error={error?.message}
                                    className="bg-white shadow-sm"
                                  />
                                )}
                              />
                            </Col>
                          )}
                          <Col md={tipoPersona === "fisica" ? 4 : 8}>
                            <label className="text-muted small fw-bold text-uppercase mb-2">
                              {tipoPersona !== "fisica" ? "Nombre de la Empresa / Razón Social" : "Apellidos"}
                            </label>
                            <Controller
                              name="cliente_apellidos"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <TextInput
                                  {...field}
                                  noFloating={true}
                                  error={error?.message}
                                  className="bg-white shadow-sm"
                                />
                              )}
                            />
                          </Col>
                        </Row>
                      </div>

                      {/* Section: Contact + Address */}
                      <div className="bg-light p-4 rounded-4 border border-light mb-4">
                        <Row className="g-3">
                          <Col md={12}>
                            <h5 className="fw-bold text-secondary mb-3">Contacto y Dirección</h5>
                          </Col>
                          <Col md={4}>
                            <label className="text-muted small fw-bold text-uppercase mb-2">Email</label>
                            <Controller
                              name="cliente_email_1"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <TextInput
                                  {...field}
                                  noFloating={true}
                                  error={error?.message}
                                  className="bg-white shadow-sm"
                                />
                              )}
                            />
                          </Col>
                          <Col md={4}>
                            <label className="text-muted small fw-bold text-uppercase mb-2">Teléfono Fijo</label>
                            <Controller
                              name="cliente_telefono_1"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <TextInput
                                  {...field}
                                  noFloating={true}
                                  error={error?.message}
                                  className="bg-white shadow-sm"
                                />
                              )}
                            />
                          </Col>
                          <Col md={4}>
                            <label className="text-muted small fw-bold text-uppercase mb-2">Teléfono Móvil</label>
                            <Controller
                              name="cliente_telefono_2"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <TextInput
                                  {...field}
                                  noFloating={true}
                                  error={error?.message}
                                  className="bg-white shadow-sm"
                                />
                              )}
                            />
                          </Col>
                        </Row>

                        <AddressFields
                          control={control}
                          errors={errors}
                          paises={paises}
                          provincias={provincias}
                          municipios={municipios}
                          poblaciones={poblaciones}
                          tipovia={tipovia}
                          loadingProv={loadProv}
                          loadingMuni={loadMuni}
                          loadingPob={loadPob}
                          idPais={idPais}
                          idProv={idProv}
                          idMuni={idMuni}
                          onPaisChange={fetchProvincias}
                          onProvinciaChange={fetchMunicipios}
                          onMunicipioChange={fetchPoblaciones}
                          prefix="cups_"
                          watchCountry="cups_id_pais"
                          watchProvince="cups_id_provincia"
                          watchMunicipio="cups_id_municipi"
                          watchPoblacion="cups_id_poblacio"
                          noFloating={true}
                          loadError={loadError}
                          prefillLabels={addressPrefillLabels}
                        />
                      </div>

                      {/* Section: Extra info & Checks */}
                      <div className="bg-white p-4 rounded-4 border mb-4">
                        <Row className="g-3 align-items-center">
                          <Col md={6}>
                            <Controller
                              name="comPostal"
                              control={control}
                              render={({ field }) => (
                                <Form.Check
                                  {...field}
                                  id="comCheckbox"
                                  type="checkbox"
                                  label={<span className="fw-bold text-secondary">Recibir factura vía postal</span>}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                  className="mb-0 custom-checkbox"
                                />
                              )}
                            />
                          </Col>
                          <Col md={6}>
                            <Controller
                              name="notificacionPostal"
                              control={control}
                              render={({ field }) => (
                                <Form.Check
                                  {...field}
                                  id="notificationPostalCheckbox"
                                  type="checkbox"
                                  label={<span className="fw-bold text-secondary">Recibir notificaciones por vía postal</span>}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                  className="mb-0 custom-checkbox"
                                  disabled={!comPostal}
                                />
                              )}
                            />
                          </Col>
                        </Row>
                      </div>
                    </fieldset>
                  </div>

                  {/* Actions Removed */}
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const OfertaPartner = (props) => {
  const { sector_type, oferta_id } = useParams();
  const formKey = `${props.authResolved ? 'auth' : 'pending'}-${props.user?.id ?? 'anon'}-${props.user?.is_internal_agent? 'internal' : 'external'}`;
  return (
    <Layout
      offerContext={{ sector_type, oferta_id }}
    >
      <OfferWizardWrapper currentStep="cliente">
        <OfertaPartnerForm key={formKey} {...props} />
      </OfferWizardWrapper>
    </Layout>
  );
};

const mapStateToProps = (state) => {
  let props = {
    authResolved: state.Auth.authResolved,
    paises: state.Auxiliares.paises,
    provincias: state.Auxiliares.provincias,
    municipios: state.Auxiliares.municipios,
    poblaciones: state.Auxiliares.poblaciones,
    tipovia: state.Auxiliares.tipovia,
    user: state.Auth.user,
  };
  return props;
};

export default connect(mapStateToProps, {
  get_paises,
  get_provincias,
  get_municipios,
  get_poblaciones,
  get_tipovia,
  setLoader,
  removeLoader,
})(OfertaPartner);
